<template>
  <div class="empty-state d-flex flex-column px-6" data-locator="empty-state">
    <div
      class="font-weight-bold text--large text-purple-darken-1 text-center mb-6"
      data-locator="ai-empty-title"
    >
      {{ $t(`${TRANSLATION_KEY_PREFIX}.hereToHelpYou`) }}
    </div>
    <div class="d-flex flex-column row-gap-4">
      <AIAssistantQuestionTypePicker
        v-if="shouldShowQuestionTypePicker"
        :disabled="isConversationLimitReached"
        @select="onSelectType"
      />

      <div v-else-if="isAskDoc" data-locator="ai-empty-ask-doc">
        <div class="text--small">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.askDoc.content`) }}
        </div>

        <ul class="px-6 text--small">
          <template v-for="(item, index) in $tm(`${TRANSLATION_KEY_PREFIX}.askDoc.list`)" :key="index">
            <li>{{ item }}</li>
          </template>
        </ul>
      </div>

      <div v-else data-locator="ai-empty-ask">
        <div>{{ $t(`${TRANSLATION_KEY_PREFIX}.ask.content`) }}</div>

        <ul class="px-6">
          <template v-for="(item, index) in $tm(`${TRANSLATION_KEY_PREFIX}.ask.list`)" :key="index">
            <li>{{ item }}</li>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useStore } from 'vuex'

import { AIAssistantMutationTypes } from '@/store/modules/ai-assistant/mutation_types'
import { AIAssistantQuestionTypes, type AIAssistantQuestionType } from '@/types/ai-assistant'

import AIAssistantQuestionTypePicker from './AIAssistantQuestionTypePicker.vue'

const emit = defineEmits<{
  'update:showFooter': [value: boolean]
}>()

const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPane'

const store = useStore()

const questionType = computed(() => store.state.aiAssistant.questionType as AIAssistantQuestionType | null)
const shouldShowQuestionTypePicker = computed(() => store.getters['aiAssistant/shouldShowQuestionTypePicker'])
const isAskDoc = computed(() => questionType.value === AIAssistantQuestionTypes.ASK_DOC)
const remainingAskDocConversations = computed(() => store.getters['aiAssistant/remainingAskDocConversations'] as number | null)
const isConversationLimitReached = computed(() => remainingAskDocConversations.value !== null && remainingAskDocConversations.value <= 0)

watch(
  shouldShowQuestionTypePicker,
  (value) => emit('update:showFooter', !value),
  { immediate: true },
)

function onSelectType(value: AIAssistantQuestionType) {
  store.commit(`aiAssistant/${AIAssistantMutationTypes.SET_QUESTION_TYPE}`, value)
}
</script>

<style scoped lang="scss">
.empty-state {
  padding-top: 120px;
}
</style>
